import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import radioConfig from '../config/radioConfig';
import { NativeRadio, usesNativeRadio } from '../nativeRadio';

const AudioPlayerContext = createContext(null);
export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const wantsPlayback = useRef(false);
  const generation = useRef(0);
  const retries = useRef(0);
  const retryTimer = useRef(null);
  const stallTimer = useRef(null);
  const retryAction = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasStream = Boolean(radioConfig.directStreamUrl);

  const clearTimers = useCallback(() => {
    clearTimeout(retryTimer.current);
    clearTimeout(stallTimer.current);
  }, []);
  const invalidateRequest = useCallback(() => { generation.current++; }, []);
  const applyNativeState = useCallback(state => {
    setIsPlaying(Boolean(state.isPlaying));
    setIsLoading(Boolean(state.isLoading));
    setHasError(Boolean(state.hasError));
  }, []);

  const startWeb = useCallback((manual = true) => {
    const audio = audioRef.current;
    if (!audio || !hasStream) return;
    clearTimers();
    const requestId = ++generation.current;
    wantsPlayback.current = true;
    if (manual) retries.current = 0;
    setHasError(false);
    setIsLoading(true);
    setIsPlaying(false);
    // Reload at the live edge; call play synchronously inside the user gesture (Safari).
    audio.load();
    const result = audio.play();
    if (result?.catch) result.catch(error => {
      if (requestId !== generation.current || !wantsPlayback.current) return;
      if (error.name === 'AbortError') return;
      setIsLoading(false);
      setIsPlaying(false);
      setHasError(true);
      // Autoplay restrictions require a new user gesture, not an endless retry.
      if (error.name === 'NotAllowedError') wantsPlayback.current = false;
    });
  }, [clearTimers, hasStream]);
  retryAction.current = () => startWeb(false);

  const play = useCallback(() => {
    if (!hasStream) { setHasError(true); return; }
    if (!usesNativeRadio) { startWeb(); return; }
    setHasError(false);
    setIsLoading(true);
    NativeRadio.play().then(applyNativeState).catch(() => {
      setIsPlaying(false); setIsLoading(false); setHasError(true);
    });
  }, [hasStream, startWeb, applyNativeState]);
  const pause = useCallback(() => {
    wantsPlayback.current = false;
    invalidateRequest();
    clearTimers();
    setIsPlaying(false);
    setIsLoading(false);
    if (usesNativeRadio) NativeRadio.pause().then(applyNativeState).catch(() => setHasError(true));
    else audioRef.current?.pause();
  }, [clearTimers, applyNativeState, invalidateRequest]);
  const toggle = useCallback(() => {
    if (isPlaying || isLoading) pause(); else play();
  }, [isPlaying, isLoading, pause, play]);

  useEffect(() => {
    if (!usesNativeRadio) return undefined;
    let disposed = false;
    let handle;
    NativeRadio.addListener('state', state => { if (!disposed) applyNativeState(state); })
      .then(listener => { if (disposed) listener.remove(); else handle = listener; });
    NativeRadio.getState().then(state => { if (!disposed) applyNativeState(state); }).catch(() => {
      if (!disposed) setHasError(true);
    });
    return () => { disposed = true; handle?.remove(); };
  }, [applyNativeState]);

  useEffect(() => {
    if (usesNativeRadio) return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;
    const reconnect = () => {
      clearTimers();
      setIsPlaying(false);
      if (!wantsPlayback.current) { setIsLoading(false); return; }
      if (retries.current >= 3 || !navigator.onLine) {
        setIsLoading(false); setHasError(true); return;
      }
      setIsLoading(true);
      retryTimer.current = setTimeout(() => retryAction.current(), 1000 * 2 ** retries.current++);
    };
    const onPlaying = () => { clearTimers(); setIsPlaying(true); setIsLoading(false); setHasError(false); };
    const onWaiting = () => {
      if (!wantsPlayback.current) return;
      setIsLoading(true);
      clearTimeout(stallTimer.current);
      stallTimer.current = setTimeout(reconnect, 15000);
    };
    const onPause = () => { clearTimers(); setIsPlaying(false); setIsLoading(false); };
    const onOnline = () => {
      if (wantsPlayback.current && (audio.paused || audio.error || audio.readyState === 0)) {
        retries.current = 0; retryAction.current();
      }
    };
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onWaiting);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', reconnect);
    audio.addEventListener('ended', reconnect);
    window.addEventListener('online', onOnline);
    return () => {
      clearTimers();
      invalidateRequest();
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onWaiting);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', reconnect);
      audio.removeEventListener('ended', reconnect);
      window.removeEventListener('online', onOnline);
    };
  }, [clearTimers, invalidateRequest]);

  useEffect(() => {
    if (usesNativeRadio || !('mediaSession' in navigator)) return undefined;
    if ('MediaMetadata' in window) navigator.mediaSession.metadata = new window.MediaMetadata({
      title: 'FM REAL 96.3 · En vivo', artist: 'Las Breñas, Chaco',
      artwork: [{ src: new URL('/icons/icon-512.png', window.location.href).href, sizes: '512x512', type: 'image/png' }],
    });
    const handlers = { play, pause, stop: pause };
    Object.entries(handlers).forEach(([action, handler]) => {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch { /* Optional browser support. */ }
    });
    return () => Object.keys(handlers).forEach(action => {
      try { navigator.mediaSession.setActionHandler(action, null); } catch { /* Optional browser support. */ }
    });
  }, [play, pause]);
  useEffect(() => {
    if (!usesNativeRadio && 'mediaSession' in navigator) navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  return (
    <AudioPlayerContext.Provider value={{ isPlaying, isLoading, hasError, hasStream, toggle, play, pause }}>
      {children}
      {hasStream && !usesNativeRadio && <audio ref={audioRef} src={radioConfig.directStreamUrl}
        preload="none" playsInline data-testid="global-audio-element" />}
    </AudioPlayerContext.Provider>
  );
};
export default AudioPlayerProvider;
